import { useEffect, useState } from 'react';
import SVG from 'react-inlinesvg';
import { cloneDeep } from 'lodash';
import frameSvg from 'assets/icons/frame.svg';
import CollapsibleListStatsHeader from 'lib/CollapsibleList/CollapsibleListStatsHeader';
import SelectOption from 'lib/SelectOption';
import { CodeNameModel, FileContent, FileList } from 'components/common/types';
import IncludeExcludeToggle from 'lib/IncludeExcludeToggle/IncludeExcludeToggle';
import CollapsibleCategoryButtons from 'lib/CollapsibleCategory/CollapsibleCategoryButtons';
import { SortDir, SortSettings } from 'components/common/types/SortDir.types';
import { updateFrameSelection } from 'utils/frameList';
import { useSelector } from 'react-redux';
import { getIsReseller } from 'store/publisher/selectors';
import UploadedFilesList from './UploadedFilesList';
import { ListOfFilesProps } from './ListOfFiles.types';

const ITEM_HEIGHT = 44;
const RESELLER_ITEM_HEIGHT = 64;

export const orderByField = <T extends CodeNameModel, K extends keyof T>(
  items: T[],
  field: K,
  direction: SortDir,
): T[] => {
  if (direction === SortDir.NONE) return items;

  return items.sort((a, b) => {
    const aValue = String(a[field]);
    const bValue = String(b[field]);

    return direction === SortDir.ASC
      ? aValue.localeCompare(bValue, undefined, { sensitivity: 'base' })
      : bValue.localeCompare(aValue, undefined, { sensitivity: 'base' });
  });
};

const getFileStats = ({ fileContents = [] }: FileList): { available: number; included: number; excluded: number } => ({
  available: fileContents.length,
  included: fileContents.filter(({ include }) => !!include).length,
  excluded: fileContents.filter(({ include }) => include === false).length,
});

const MediaOwnerLabel: React.FC<{ isReseller: boolean; mediaOwner?: string; name: string }> = ({
  isReseller,
  mediaOwner,
  name,
}) => (
  <div>
    {isReseller && mediaOwner && <div>{mediaOwner}:</div>}
    {name}
  </div>
);

const ListOfFiles: React.FC<ListOfFilesProps> = ({
  uploadedListOfFiles,
  setUploadedListOfFiles,
  onToggleCategory,
  openCategories,
  onRemoveFile,
  isIncludeExcludeDisabled = false,
}) => {
  const isReseller = useSelector(getIsReseller);

  const [sortDirection, setSortDirection] = useState(SortDir.ASC);
  const [currentUploadedListOfFiles, setCurrentUploadedListOfFiles] = useState(uploadedListOfFiles);

  const itemHeight = isReseller ? RESELLER_ITEM_HEIGHT : ITEM_HEIGHT;

  useEffect(() => {
    setCurrentUploadedListOfFiles(uploadedListOfFiles);
  }, [uploadedListOfFiles]);

  const onCategoryDirectionSort = (fileName: string, direction: SortDir): void => {
    const uploadedFiles = cloneDeep(currentUploadedListOfFiles);

    setSortDirection(SortSettings[sortDirection].nextDir);

    const fileIndex = uploadedFiles.findIndex(({ name }) => name === fileName);
    const contents = uploadedFiles[fileIndex].fileContents;

    if (!contents) return;

    const sorted = orderByField(contents, 'name', direction);

    uploadedFiles[fileIndex].fileContents = sorted;

    setCurrentUploadedListOfFiles(uploadedFiles);
  };

  const isOptionSelected = (code: string, index: number): boolean =>
    !!uploadedListOfFiles?.[index]?.fileContents?.find((option) => option.code === code);

  const onOptionIncludeExcludeChange = (value: CodeNameModel, index: number): void => {
    const uploadedFiles = cloneDeep(uploadedListOfFiles);
    const contents = uploadedFiles[index].fileContents;

    if (!contents) return;

    const updatedContents = updateFrameSelection(contents, value, false);

    uploadedFiles[index].fileContents = updatedContents;
    uploadedFiles[index].include = undefined;

    setUploadedListOfFiles(uploadedFiles);
  };

  const onEntireFileIncludeExclude = ({ name, include }: CodeNameModel): void => {
    if (include === undefined) return;

    const uploadedFiles = cloneDeep(uploadedListOfFiles);
    const currentFile = uploadedFiles.find((file) => file.name === name);

    if (!currentFile?.fileContents) return;

    currentFile.fileContents = currentFile.fileContents.map((content) => ({ ...content, include }));
    currentFile.include = include;

    setUploadedListOfFiles(uploadedFiles);
  };

  const handleIncludeExcludeChange = !isIncludeExcludeDisabled
    ? (value: CodeNameModel, fileIndex: number) => onOptionIncludeExcludeChange(value, fileIndex)
    : undefined;

  const renderItem = (
    { code, name, include, mediaOwner }: FileContent,
    fileIndex: number,
    style: React.CSSProperties,
  ): React.ReactElement => {
    return (
      <div
        className="border-b border-neutral-950-opacity-10 last:border-b-0"
        key={`uploaded-file-option-${code}-${mediaOwner}`}
        style={style}
      >
        <SelectOption
          value={{ code, name: code, include, mediaOwner }}
          isSelected={isOptionSelected(code, fileIndex)}
          isMultiple
          hasCheckbox={false}
          isIncludeExcludeAlwaysVisible
          isDeselectAllowed={false}
          onIncludeExcludeChange={(value) => handleIncludeExcludeChange?.(value, fileIndex)}
        >
          <MediaOwnerLabel isReseller={isReseller} mediaOwner={mediaOwner} name={name} />
        </SelectOption>
      </div>
    );
  };

  const renderHeaderComponent = (file: FileList, _fileIndex: number, isHovered: boolean): React.ReactElement => {
    const { available, included, excluded } = getFileStats(file);

    return (
      <>
        {isHovered && <CollapsibleCategoryButtons onRemove={() => onRemoveFile(file.name)} />}
        <CollapsibleListStatsHeader
          available={available}
          included={included}
          excluded={excluded}
          availableLengthSuffix={<SVG src={frameSvg} />}
        />
        <IncludeExcludeToggle
          option={{ ...file, code: file.name }}
          onIncludeExcludeChange={onEntireFileIncludeExclude}
          isAlwaysVisible
          isSelected={file.include !== undefined}
          isOptionNameSelectable={false}
          isHovered={false}
          isDeselectAllowed={false}
        />
      </>
    );
  };

  return (
    <UploadedFilesList
      uploadedFiles={currentUploadedListOfFiles}
      openCategories={openCategories}
      onToggleCategory={onToggleCategory}
      onSort={onCategoryDirectionSort}
      sortDirection={sortDirection}
      itemHeight={itemHeight}
      renderItem={renderItem}
      renderHeaderComponent={renderHeaderComponent}
    />
  );
};

export default ListOfFiles;
